import { AspectRatio, Box, LinkBox, LinkOverlay, StyleProps, Tag, Text } from "@chakra-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import React, { useRef, useState } from "react"
import { useRecoilValue } from "recoil"
import { MAXIMUM_ALLOWED_ARTICLES_INLISTS, pages, pdpQueryParams } from "../../../../../common/constants/constants"
import { attributes, productImageViews } from "../../../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import useList from "../../../../../common/hooks/useList"
import { defaultListIdState } from "../../../../../common/store/defaultListIdStore"
import { onboardingStepIndexState } from "../../../../../common/store/onboardingStore"
import { ProductImageViewType } from "../../../../../common/types/global"
import { FhResponseData } from "../../../../../common/types/search"
import { isImageCached, preloadImage } from "../../../../../common/utils/image-utils"
import {
  checkImplicitRangesMatch,
  flattenAttributes,
  getReplacementInfo,
} from "../../../../../common/utils/search-utils"
import CustomImage from "../../../../shared/CustomImage"
import CustomLink from "../../../../shared/CustomLink"
import MotionEnabledComponent from "../../../../shared/MotionEnabledComponent/MotionEnabledComponent"
import NewIndicator from "../../../../shared/NewIndicator"
import PhasingOutIndicator from "../../../../shared/PhasingOutIndicator"
import AddToListButton from "./AddToListButton/AddToListButton"
import MainImageToggle from "./MainImageToggle/MainImageToggle"
import ThumbnailsRow from "./ThumbnailsRow/ThumbnailsRow"

const {
  IMAGE,
  TECH_IMAGE,
  ARTICLE_IMAGE,
  NAME,
  PRODUCT_ID,
  ALL_ARTICLES,
  SYSTEMS_CATEGORY,
  IS_NEW,
  SERIES,
  ARTICLE_NUMBER,
  ARTICLE_REPLACEMENT_FOR,
  ARTICLE_REPLACED_BY,
  PHASE_OUT_INDICATOR,
  PHASE_OUT_INDICATOR_ARTICLE,
  MARKET_LAUNCH_DATE,
} = attributes

const PRODUCT_TILE_WIDTH = "61"
const TECH_IMAGE_PADDING = "4" // we need 1em padding on all sides for technical images

const MotionCustomImage = MotionEnabledComponent(CustomImage)

const getImageStyles = (imageType: ProductImageViewType): StyleProps => {
  const productImageWidth = PRODUCT_TILE_WIDTH
  const techImageWidth = String(Number(PRODUCT_TILE_WIDTH) - 2 * Number(TECH_IMAGE_PADDING)) // accomodate the padding for the technical images
  const imageStyles = {
    height: "auto",
    m: "auto",
  }
  switch (imageType) {
    case productImageViews.PRODUCT:
      return {
        ...imageStyles,
        maxW: productImageWidth,
        maxH: productImageWidth,
      }
    case productImageViews.TECH:
      return {
        ...imageStyles,
        maxW: techImageWidth,
        maxH: techImageWidth,
      }
  }
}

type Props = {
  product: FhResponseData
  implicitRanges: FhResponseData
  searchTerm: string
  handleTileClick: (e: React.MouseEvent<HTMLElement>, itemId: string, pdpLinkQueryParamsUri?: string) => void
  index?: number
  selectedColors: string | null
}

const ResultsProductTile = ({ product, implicitRanges, searchTerm, handleTileClick, index, selectedColors }: Props) => {
  const { t } = useBrandedTranslation()

  /* Ref for the tile container */
  const containerRef = useRef(null)

  const onboardingStepIndex = useRecoilValue(onboardingStepIndexState)

  const attributes = flattenAttributes(product.attribute)

  // Articles come from FH as a string of article ids, delimited by ';'
  const articlesCount = attributes[ALL_ARTICLES]?.split(";").length
  const shouldShowArticleDetails =
    articlesCount === 1 || checkImplicitRangesMatch(product.attribute, implicitRanges, selectedColors)
  const hasNewTag = attributes[IS_NEW] === "1"
  const seriesTag = attributes[SERIES]
  const hasTag = hasNewTag || seriesTag
  const isProductPhasedOut = attributes[PHASE_OUT_INDICATOR] === "1"
  const isArticlePhasedOut = attributes[PHASE_OUT_INDICATOR_ARTICLE] === "1"
  const techImages = attributes[TECH_IMAGE]?.split(";")
  const productImage = shouldShowArticleDetails ? attributes[ARTICLE_IMAGE] : attributes[IMAGE]
  const replacementFor = shouldShowArticleDetails && getReplacementInfo(product.attribute, ARTICLE_REPLACEMENT_FOR)
  const replacedBy = shouldShowArticleDetails && getReplacementInfo(product.attribute, ARTICLE_REPLACED_BY)
  const pdpLinkQueryParamsUri =
    articlesCount > 1 && shouldShowArticleDetails
      ? "?" +
        new URLSearchParams({
          [pdpQueryParams.ARTICLES]: attributes[ARTICLE_NUMBER],
          [pdpQueryParams.SEARCH_TERM]: searchTerm,
        }).toString()
      : ""

  const defaultListId = useRecoilValue(defaultListIdState)
  const { listData } = useList(defaultListId)

  //a function to get the number of free positions in a list
  //invoked when the user clicks on the add to list button
  const shouldAllowAddToList = () => {
    const listArticleCount = listData?.totalArticles ?? 0
    //subtract the articles which are already in the list
    const articlesToBeAddedCount =
      articlesCount -
      (listData?.products
        ?.find((prd) => prd.id === attributes[PRODUCT_ID])
        ?.articles?.filter((art) => art.quantity && art.quantity > 0)?.length ?? 0)
    return listArticleCount + articlesToBeAddedCount <= MAXIMUM_ALLOWED_ARTICLES_INLISTS
  }

  const [imageView, setImageView] = useState<ProductImageViewType>(productImageViews.PRODUCT)
  const [mainImage, setMainImage] = useState(productImage)
  //set initial opacity of the image to 1 so that they load in full opacity or it might look laggy
  const imageOpacity = useRef(1)

  const newTagTooltipText = attributes[MARKET_LAUNCH_DATE]
    ? `${t("pdp-newLabelTip", { count: articlesCount })} ${attributes[MARKET_LAUNCH_DATE]}`
    : null

  const handleImageToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    hideContainerHighlight()

    if (imageOpacity.current) imageOpacity.current = 0 //set subsequent image opacity to 0 to make a cross-fade effect

    const isProductImageView = imageView === productImageViews.PRODUCT
    setMainImage(isProductImageView ? techImages[0] : productImage)
    setImageView(isProductImageView ? productImageViews.TECH : productImageViews.PRODUCT)
  }

  const handleImagePreload = () => {
    const nextMainImage = imageView === productImageViews.PRODUCT ? techImages[0] : productImage
    // If image is not cached in browser, start loading the image in the background
    if (!isImageCached(nextMainImage)) preloadImage(nextMainImage)
  }

  const handleProductTileClick = (e: React.MouseEvent<HTMLElement>) => {
    handleTileClick(e, attributes[PRODUCT_ID], pdpLinkQueryParamsUri)
  }

  /* For buttons inside the tile which does not redirect to the product page
    hide the default highlighting applied by the browser */
  const hideContainerHighlight = () => {
    /* Hide the highlighting until the active state is executed */
    if (containerRef?.current) containerRef.current.style.webkitTapHighlightColor = "transparent"

    /* Return to the initial value after the active state is executed */
    setTimeout(() => {
      containerRef.current.style.webkitTapHighlightColor = "initial"
    }, 100)
  }

  return (
    <LinkBox
      id={index === 0 ? "product-tile" : null} // Add id only for the 1st tyle needed for user onboarding
      as="article"
      position="relative"
      w="100%"
      minW="76.5"
      py={{ base: "4", sm: "6" }}
      px={{ base: "0", sm: "6", md: "8" }}
      mx={{ base: "auto", sm: "0" }}
      borderRight="1px solid"
      borderBottom="1px solid"
      borderColor="gray.300"
      _hover={{
        "@media (hover: hover) and (pointer: fine)": { color: "brand.500" },
        ".action-button": { display: "inline-block" },
      }}
      sx={{
        "@media (hover: none), (pointer: coarse)": { ".action-button": { display: "inline-block" } },
        button: { display: onboardingStepIndex === 7 && index === 0 ? "inline-block" : "" }, // Add extra styles to show the two option buttons needed for user onboarding
      }}
    >
      <CustomLink href={`/${pages.PRODUCT}/${attributes[PRODUCT_ID]}${pdpLinkQueryParamsUri}`}>
        <LinkOverlay
          _active={{ color: "brand.700" }}
          ref={containerRef}
          onClick={handleProductTileClick}
          onAuxClick={handleProductTileClick}
        >
          <Box>
            <Box w={PRODUCT_TILE_WIDTH} h="auto" mb="6" mt={{ base: "2", sm: "0" }} mx="auto" overflow="clip">
              <AddToListButton
                articleId={shouldShowArticleDetails ? attributes[ARTICLE_NUMBER] : null}
                productId={attributes[PRODUCT_ID]}
                hideContainerHighlight={hideContainerHighlight}
                shouldAllowAddToList={shouldAllowAddToList}
              />
              {!!techImages?.length && (
                <MainImageToggle
                  imageView={imageView}
                  handleImageToggle={handleImageToggle}
                  handleImagePreload={handleImagePreload}
                />
              )}
              <AspectRatio ratio={1} width={PRODUCT_TILE_WIDTH}>
                <AnimatePresence>
                  <MotionCustomImage
                    as={motion.img}
                    src={mainImage}
                    alt={attributes[NAME]}
                    // Make the browser prioritize tech images
                    loading={imageView === productImageViews.TECH ? "eager" : "lazy"}
                    key={mainImage}
                    initial={{ opacity: imageOpacity.current }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    {...getImageStyles(imageView)}
                  />
                </AnimatePresence>
              </AspectRatio>
            </Box>
            {hasTag && (
              <Box position="relative" mt="-9" mb="3">
                {hasNewTag && <NewIndicator tooltipLabel={newTagTooltipText} tooltipPlacement="top-start" />}
                {seriesTag && <Tag variant="series">{seriesTag}</Tag>}
              </Box>
            )}
            <Text fontSize="2xs" mb={2} color="gray.700">
              {attributes[SYSTEMS_CATEGORY]}
            </Text>
            <Text lineHeight={{ base: "1.27", md: "1.33", lg: "1.23" }} fontWeight="bold" fontSize="2xs" mb={2}>
              {attributes[NAME]}
            </Text>
            {shouldShowArticleDetails ? (
              <>
                <Text fontSize="2xs" color="gray.700">
                  {attributes[ARTICLE_NUMBER]}
                </Text>
                {replacedBy && (
                  <Text fontSize="2xs" color="gray.700" mt="2" noOfLines={2}>
                    {`${t("pdp-article-replacedBy")} ${replacedBy}`}
                  </Text>
                )}
                {replacementFor && (
                  <Text fontSize="2xs" color="gray.700" mt={replacedBy ? "0" : "2"} noOfLines={2}>
                    {`${t("pdp-article-replacementFor")} ${replacementFor}`}
                  </Text>
                )}
                {isArticlePhasedOut && (
                  <Box mt="2">
                    <PhasingOutIndicator />
                  </Box>
                )}
              </>
            ) : (
              <>
                {!!articlesCount && (
                  <Text fontSize="2xs" color="gray.700">
                    {articlesCount} {t("pdp-tile-article-unit", { count: articlesCount })}
                  </Text>
                )}
                {isProductPhasedOut && (
                  <Box mt="2">
                    <PhasingOutIndicator />
                  </Box>
                )}
              </>
            )}
            {!!techImages?.length && (
              <Box pt="3" minH="13.5">
                {imageView === productImageViews.TECH && (
                  <ThumbnailsRow
                    images={techImages}
                    mainImage={mainImage}
                    setMainImage={setMainImage}
                    hideContainerHighlight={hideContainerHighlight}
                  />
                )}
              </Box>
            )}
          </Box>
        </LinkOverlay>
      </CustomLink>
    </LinkBox>
  )
}

export default ResultsProductTile
